import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import exerciseImageOverrides from '../data/exerciseImageOverrides.json';
import { Input } from '../components/ui/input';
import { Search, ArrowLeft } from 'lucide-react';

const ExerciseGallery = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, has-image, missing

  const exercises = useMemo(() => {
    return Object.entries(exerciseImageOverrides)
      .map(([name, url]) => ({ name, url }))
      .filter(ex => {
        if (search && !ex.name.toLowerCase().includes(search.toLowerCase())) return false;
        if (filter === 'has-image' && !ex.url) return false;
        if (filter === 'missing' && ex.url) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [search, filter]);

  const total = Object.keys(exerciseImageOverrides).length;
  const withImage = Object.values(exerciseImageOverrides).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-stone-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-stone-400 hover:text-blood-400 text-sm mb-4 transition-colors"
          data-testid="back-to-admin"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin Dashboard
        </button>
        <h1 className="text-2xl font-bold text-blood-400 mb-2 tracking-wider">EXERCISE IMAGE GALLERY</h1>
        <p className="text-stone-400 text-sm mb-6">
          {withImage}/{total} legacy imported image URLs are cataloged here for review. Workout screens only show artwork explicitly approved in <code className="text-blood-400">verifiedExerciseMedia.js</code>; unreviewed artwork stays hidden.
        </p>

        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-stone-900 border-stone-700 text-white"
              placeholder="Search exercises..."
              data-testid="gallery-search"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'has-image', 'missing'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  filter === f
                    ? 'bg-blood-900/30 border border-blood-600 text-blood-400'
                    : 'bg-stone-900 border border-stone-700 text-stone-400 hover:border-stone-600'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? `All (${total})` : f === 'has-image' ? `Has Image (${withImage})` : `Missing (${total - withImage})`}
              </button>
            ))}
          </div>
        </div>

        <div className="text-stone-500 text-sm mb-4">{exercises.length} exercises shown</div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {exercises.map(ex => (
            <div
              key={ex.name}
              className={`rounded-lg border overflow-hidden ${
                ex.url ? 'border-stone-800 bg-stone-900' : 'border-amber-900/50 bg-amber-950/20'
              }`}
              data-testid={`exercise-card-${ex.name}`}
            >
              <div className="aspect-square bg-white flex items-center justify-center p-1">
                {ex.url ? (
                  <img
                    src={ex.url}
                    alt={ex.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div
                  className={`w-full h-full items-center justify-center text-stone-400 text-xs text-center p-2 ${ex.url ? 'hidden' : 'flex'}`}
                >
                  No image
                </div>
              </div>
              <div className="p-2">
                <p className="text-stone-200 text-xs font-bold leading-tight truncate" title={ex.name}>
                  {ex.name}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExerciseGallery;
